import React, { useState, useEffect } from 'react';
import { Building2, ChevronRight, Database, FileCheck, Lightbulb, PlayCircle, Settings2, Check } from 'lucide-react';
import { login, logout } from '../firebase';
import auth from '../firebase';

// Logo Component
const GPTfyLogo = () => (
  <div className="flex items-center space-x-2">
    <div className="bg-blue-600 text-white font-bold py-1 px-3 rounded">GPTfy</div>
  </div>
);

// Step Indicator Component
const StepIndicator = ({ currentStep, totalSteps }) => (
  <div className="flex items-center space-x-2 text-sm text-gray-500">
    <span className="font-medium">Step {currentStep + 1}</span>
    <span>of</span>
    <span className="font-medium">{totalSteps}</span>
  </div>
);

// Individual Step Component
const WizardStep = ({ active, completed, icon: Icon, title, description }) => (
  <div 
    className={`flex items-start space-x-4 p-4 rounded-lg transition-all 
    ${active ? 'bg-blue-50' : completed ? 'opacity-50' : 'opacity-40'}`}
  >
    <div className={`p-2 rounded-full 
      ${active ? 'bg-blue-600 text-white' : 
        completed ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
    >
      <Icon className="w-6 h-6" />
    </div>
    <div className="flex-1">
      <h3 className={`font-semibold ${active ? 'text-blue-900' : 'text-gray-700'}`}>
        {title}
      </h3>
      <p className={`text-sm ${active ? 'text-blue-800' : 'text-gray-500'}`}>
        {description}
      </p>
    </div>
    {active && <ChevronRight className="w-5 h-5 text-blue-600" />}
  </div>
);

// Use Case Card Component
const UseCaseCard = ({ useCase }) => (
  <div className="border rounded-lg p-4 hover:bg-gray-50">
    <div className="flex flex-col sm:flex-row items-start space-y-2 sm:space-y-0 sm:space-x-4">
      <input type="radio" name="useCase" className="mt-1" />
      <div className="flex-1">
        <h4 className="font-medium text-gray-900">{useCase.title}</h4>
        <p className="text-sm text-gray-600 mt-1">{useCase.description}</p>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className={`px-2 py-1 rounded-full text-xs ${
            useCase.impact === 'High' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
          }`}>
            {useCase.impact} Impact
          </span>
          <span className={`px-2 py-1 rounded-full text-xs ${
            useCase.complexity === 'Low' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {useCase.complexity} Complexity
          </span>
          <span className="text-xs text-gray-500">{useCase.roi}</span>
        </div>
      </div>
    </div>
  </div>
);

// Sample Record Card Component
const SampleRecordCard = ({ record }) => (
  <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
    <div className="flex items-center space-x-3">
      <input type="checkbox" className="rounded text-blue-600" />
      <div>
        <div className="font-medium">{record.name}</div>
        <div className="text-sm text-gray-500">{record.details}</div>
      </div>
    </div>
    <span className={`px-2 py-1 rounded-full text-xs ${
      record.quality === 'High' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
    }`}>
      {record.quality} Quality
    </span>
  </div>
);

// Wizard Content Component
const WizardContent = ({ step }) => {
  const useCases = [
    {
      title: 'Account 360° View',
      description: 'Generate comprehensive account insights from cases and opportunities',
      impact: 'High',
      complexity: 'Low',
      roi: '85 hours/month saved'
    },
    {
      title: 'Smart Case Summaries',
      description: 'Auto-generate concise case summaries with sentiment analysis',
      impact: 'High',
      complexity: 'Medium',
      roi: '45 hours/month saved'
    },
    {
      title: 'Sales Opportunity Insights',
      description: 'AI-powered next best actions for open opportunities',
      impact: 'Medium',
      complexity: 'Low',
      roi: '30 hours/month saved'
    }
  ];

  const sampleRecords = [
    {
      name: 'Acme Corp',
      type: 'Account',
      quality: 'High',
      details: 'Complete profile, multiple cases & opportunities'
    },
    {
      name: 'TechStart Inc',
      type: 'Account',
      quality: 'Medium',
      details: '3 active cases, recent engagement'
    },
    {
      name: 'Global Services Ltd',
      type: 'Account',
      quality: 'High',
      details: 'Rich interaction history, multiple touchpoints'
    }
  ];

  const contentMap = {
    0: (
      <div className="space-y-6">
        <div className="border rounded-lg bg-white">
          <div className="p-4 bg-gray-50 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Tell Us About Your Business</h3>
            <p className="text-sm text-gray-600">Help us understand your needs better</p>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
              <select className="w-full p-2 border rounded-md">
                <option>Financial Services</option>
                <option>Healthcare & Life Sciences</option>
                <option>Manufacturing</option>
                <option>Technology</option>
                <option>Retail & Consumer Goods</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Process</label>
              <textarea 
                className="w-full p-2 border rounded-md h-24"
                placeholder="Describe the business process you want to enhance..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Key Objectives</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  'Improve Customer Experience',
                  'Increase Efficiency',
                  'Reduce Costs',
                  'Drive Revenue',
                  'Enhance Decision Making',
                  'Automate Manual Tasks'
                ].map(objective => (
                  <label 
                    key={objective} 
                    className="flex items-center p-2 border rounded-lg space-x-2 hover:bg-gray-50"
                  >
                    <input type="checkbox" className="rounded text-blue-600" />
                    <span className="text-sm text-gray-700">{objective}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
    1: (
      <div className="space-y-4">
        <div className="border rounded-lg bg-white">
          <div className="p-4 bg-gray-50 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Select Objects</h3>
            <p className="text-sm text-gray-600">Choose up to 3 related Salesforce objects</p>
          </div>
          <div className="p-4 space-y-3">
            {[
              { name: 'Account', fields: '7/78', main: true },
              { name: 'Cases', fields: '18/68', main: false },
              { name: 'Opportunities', fields: '11/61', main: false }
            ].map(obj => (
              <div key={obj.name} className={`flex justify-between items-center p-3 border rounded-lg ${obj.main ? '' : 'ml-6'}`}>
                <div className="flex items-center space-x-3">
                  <Database className={`w-5 h-5 ${obj.main ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div>
                    <div className="text-gray-900">{obj.name}</div>
                    <div className="text-sm text-gray-500">{obj.fields} fields selected</div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
    2: (
      <div className="space-y-4">
        <div className="border rounded-lg bg-white">
          <div className="p-4 bg-gray-50 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Select Sample Records</h3>
            <p className="text-sm text-gray-600">Choose 3-5 representative records for analysis</p>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex space-x-2">
              <div className="flex-1">
                <input 
                  type="text" 
                  placeholder="Search records..." 
                  className="w-full p-2 border rounded-md"
                />
              </div>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Search
              </button>
            </div>
            <div className="space-y-2">
              {sampleRecords.map(record => (
                <SampleRecordCard key={record.name} record={record} />
              ))}
            </div>
          </div>
        </div>
      </div>
    ),
    3: (
      <div className="space-y-4">
        <div className="border rounded-lg bg-white">
          <div className="p-4 bg-gray-50 border-b">
            <h3 className="text-lg font-semibold text-gray-900">AI Analysis Results</h3>
            <p className="text-sm text-gray-600">Here's what our AI found in your data</p>
          </div>
          <div className="p-4 space-y-4">
            <div className="bg-green-50 border border-green-100 rounded-lg p-4">
              <h4 className="text-green-800 font-medium">Data Quality Assessment</h4>
              <div className="mt-2 space-y-2">
                {[
                  'Rich data across selected records',
                  'Consistent field usage patterns',
                  'Good relationship coverage'
                ].map(item => (
                  <div key={item} className="flex items-center text-green-700">
                    <Check className="w-4 h-4 mr-2" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Key Patterns Detected:</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { pattern: 'Regular Customer Interactions', details: '2-3 touchpoints per week' },
                  { pattern: 'Structured Case Management', details: 'Clear resolution paths' },
                  { pattern: 'Opportunity Progression', details: 'Consistent stage updates' },
                  { pattern: 'Response Time Patterns', details: 'Average 4 hour response' }
                ].map(item => (
                  <div key={item.pattern} className="p-3 bg-blue-50 rounded-lg">
                    <div className="font-medium text-blue-900">{item.pattern}</div>
                    <div className="text-sm text-blue-700 mt-1">{item.details}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
    4: (
      <div className="space-y-4">
        <div className="border rounded-lg bg-white">
          <div className="p-4 bg-gray-50 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Recommended Use Cases</h3>
            <p className="text-sm text-gray-600">Based on your business context and data patterns</p>
          </div>
          <div className="p-4 space-y-4">
            {useCases.map(useCase => (
              <UseCaseCard key={useCase.title} useCase={useCase} />
            ))}
          </div>
        </div>
      </div>
    ),
    5: (
      <div className="space-y-4">
        <div className="border rounded-lg bg-white">
          <div className="p-4 bg-gray-50 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Test Your AI Prompt</h3>
            <p className="text-sm text-gray-600">Try out and refine your generated prompt</p>
          </div>
          <div className="p-4 space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg space-y-2">
              <div className="text-sm font-medium text-gray-700">Generated Prompt:</div>
              <div className="font-mono text-sm text-gray-900 whitespace-pre-wrap">
{`Generate a comprehensive overview of account {{Account.Name}} including:
- Summary of recent cases and their status
- Key opportunities and next steps
- Customer sentiment analysis
- Recommended actions based on history

Format the response as an HTML table with clear sections.`}
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Select a record to test</label>
              <div className="flex space-x-2">
                <div className="flex-1">
                  <input 
                    type="text"
                    placeholder="Search records..."
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                  <PlayCircle className="w-4 h-4" />
                  <span>Run GPTfy</span>
                </button>
              </div>
            </div>

            <div className="border rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-medium text-gray-700">Sample Output</span>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Processing: 2.3s</span>
                </div>
              </div>
              <div className="prose prose-sm max-w-none text-gray-600">
                <p className="italic text-gray-400">AI response will appear here...</p>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm p-2 bg-blue-50 rounded-lg">
              <span className="text-blue-600">Need to modify the prompt?</span>
              <button className="text-blue-700 hover:text-blue-800 font-medium">
                Edit Prompt
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  };

  return contentMap[step] || null;
};

const AIUseWizard = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && user.email?.endsWith('@cloudcompliance.app')) {
        setUser(user);
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const steps = [
    {
      icon: Building2,
      title: "Tell Us About Your Business",
      description: "Share your industry and goals"
    },
    {
      icon: Database,
      title: "Select Your Objects",
      description: "Choose the Salesforce objects to enhance"
    },
    {
      icon: FileCheck,
      title: "Share Example Records",
      description: "Provide sample data for analysis"
    },
    {
      icon: Settings2,
      title: "Review AI Analysis",
      description: "See what patterns we've found"
    },
    {
      icon: Lightbulb,
      title: "Pick Your Use Cases",
      description: "Choose from recommended solutions"
    },
    {
      icon: PlayCircle,
      title: "Test Your AI Prompt",
      description: "Try out and refine your solution"
    }
  ];

  // Render login page if user is not authenticated
  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <h1 className="text-2xl font-semibold text-gray-700 mb-4">Restricted Access</h1>
        <p className="text-gray-500 mb-4">Please log in with your CloudCompliance account.</p>
        <button
          onClick={login}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Login with Google
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-sm border">
        <div className="p-4 sm:p-6 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-4 space-y-4 sm:space-y-0">
            <div>
              <div className="flex items-center space-x-2">
                <GPTfyLogo />
                <span className="text-xl font-semibold text-gray-700">
                  Use Case Wizard
                </span>
              </div>
              <p className="text-gray-600 mt-1">
                Let's create the perfect AI solution for your needs
              </p>
            </div>
            <div className="flex items-center space-x-4 w-full sm:w-auto justify-between sm:justify-start">
              <StepIndicator currentStep={currentStep} totalSteps={steps.length} />
              <button 
                onClick={logout} 
                className="text-sm text-red-500 hover:text-red-700"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Main content */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-6">
            {/* Steps sidebar */}
            <div className="col-span-1 sm:col-span-2 overflow-x-auto sm:overflow-x-visible">
              <div className="flex sm:flex-col space-x-4 sm:space-x-0 sm:space-y-2 sm:border-r sm:pr-6">
                {steps.map((step, index) => (
                  <div key={index} className="min-w-[250px] sm:min-w-0">
                    <WizardStep
                      active={index === currentStep}
                      completed={index < currentStep}
                      {...step}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Step content */}
            <div className="col-span-1 sm:col-span-3">
              <WizardContent step={currentStep} />
            </div>
          </div>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row justify-between pt-4 border-t space-y-2 sm:space-y-0">
            <button
              className="w-full sm:w-auto px-4 py-2 text-gray-600 hover:text-gray-900 flex items-center justify-center sm:justify-start space-x-2 disabled:opacity-50"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              <span>Back</span>
            </button>
            <button
              className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center sm:justify-start space-x-2 disabled:opacity-50"
              onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
              disabled={currentStep === steps.length - 1}
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIUseWizard; 